import type { UserData } from '@/types/user'

/**
 * Find an object in an array by matching a key's value
 */
export const objectKeyVal = <T, K extends keyof T>(
  val: T[K] | null,
  array: T[],
  key: K
): T | undefined => {
  if (!val || !array?.length) return undefined
  return array.find(item => item[key] === val)
}

/**
 * Show a message to the user
 */
export const showMessage = (title: string = '', msg: string = ''): void => {
  // TODO: Implement actual message display logic
  console.log(`${title}: ${msg}`)
}

export const verifyResult = (result: unknown): boolean => {
  if (Array.isArray(result)) {
    return result.length >= 2 && Boolean(result[0])
  }
  if (typeof result === 'object' && result !== null) {
    return (
      'success' in result && Boolean((result as { success: boolean }).success)
    )
  }
  return false
}

export const resultReturn = (result: unknown): unknown | null => {
  if (Array.isArray(result)) {
    return result.length >= 3 ? result[2] : null
  }
  if (typeof result === 'object' && result !== null) {
    return 'data' in result ? (result as { data: unknown }).data : null
  }
  return null
}

/**
 * Obfuscates a string by showing only the last 4 characters and replacing the rest with asterisks
 * @param {string} str - The string to obfuscate
 * @param {number} visibleChars - Number of characters to show at the end (default: 4)
 * @param {string} maskChar - Character to use for masking (default: '*')
 * @returns {string} - The obfuscated string
 */
export const obfuscateString = (
  str: string,
  visibleChars: number = 4,
  maskChar: string = '*'
): string => {
  if (!str || typeof str !== 'string') {
    return ''
  }

  if (str.length <= visibleChars) {
    return str
  }

  const visiblePart = str.slice(-visibleChars)
  const maskLength = Math.min(str.length - visibleChars, 8) // Limit mask length for readability
  const mask = maskChar.repeat(maskLength)

  return `${mask}${visiblePart}`
}

/**
 * Check if a user has super admin privileges based on email domain
 * @param {Object} user - User object with profile containing emailAddress
 * @returns {boolean} Whether the user is a super admin
 */
export const isUserTeamMember = (user: UserData): boolean => {
  if (!user?.profile?.emailAddress) {
    return false
  }

  const email = user.profile.emailAddress.toLowerCase().trim()
  return email.endsWith('@myassetmap.com')
}
